import { useQuery } from '@tanstack/react-query';
import { ApiEndpoints } from 'types/api';
import { Review } from 'types/review';
import { WidgetRequest, WidgetResponse } from 'types/widget';

import { useSettings } from 'components/SettingsProvider';

const INITIAL_IMAGES_COUNT = 7;

async function fetchReviews(request: WidgetRequest, timestamp: number) {
  const requestHash = btoa(request as any);
  const response = await fetch(
    Shopify.designMode
      ? ApiEndpoints.WIDGET_DEMO
      : `${ApiEndpoints.WIDGET}/${timestamp}/${requestHash}/reviews.js`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
    },
  );

  if (response.status === 200) {
    return await response.json();
  }
}

export function useImageGrid(loadAll: boolean) {
  const { utils } = useSettings();
  const request: WidgetRequest = {
    components: {
      images: true,
      rating_details: true,
    },
    count: !loadAll ? INITIAL_IMAGES_COUNT : 1000,
    filters: {
      has_images: true,
      product_ids: utils.product?.id ? [utils.product?.id] : [],
      rating_from: null,
      rating_to: null,
      verified: null,
    },
    page: 1,
    sort: 'DATE_DESC',
  };

  const {
    data = {
      items: initialResponse,
    },
  } = useQuery<WidgetResponse>(
    ['reviews', request],
    () => fetchReviews(request, utils.cacheKey),
    {
      keepPreviousData: true,
      onError: () => {
        console.error('Webtex Reviews: Cannot fetch reviews');
      },
    },
  );

  if (!data) {
    return {
      images: [],
      reviews: initialResponse,
    };
  }

  const images = data.items.reduce((images, item) => {
    item.images.forEach(({ image_key }) => {
      images[image_key] = item.id;
    });

    return images;
  }, {} as { [key: string]: number });

  const reviews = data.items.reduce((reviews, item) => {
    reviews[item.id] = { ...item, images: [] };

    return reviews;
  }, {} as { [key: number]: Review });

  return {
    images: Object.entries(images),
    reviews,
  };
}

const initialResponse: Review[] = [];
